import dayjs from 'dayjs';

/**
 * Checks if a string value has a valid date structure and represents a valid date
 *
 * Validates that the string:
 * - Matches common date formats (ISO 8601, DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, etc.)
 * - Contains proper date separators (-, /, or T for ISO timestamps)
 * - Is recognized as a valid date by dayjs
 *
 * Supported formats:
 * - YYYY-MM-DD (ISO): 2026-01-08
 * - YYYY-MM-DDTHH:mm:ss (ISO with time): 2026-01-08T10:30:00
 * - YYYY/MM/DD: 2026/01/08
 * - DD/MM/YYYY: 08/01/2026
 * - MM/DD/YYYY: 01/08/2026
 * - DD-MM-YYYY: 08-01-2026
 * - MM-DD-YYYY: 01-08-2026
 *
 * @param {string} value - String value to check
 * @returns {boolean} True if the value is a valid date with proper structure, false otherwise
 *
 * @example
 * isDateString('2026-01-08'); // Returns: true
 * isDateString('2026/01/08'); // Returns: true
 * isDateString('08/01/2026'); // Returns: true
 * isDateString('2026-01-08T10:30:00'); // Returns: true
 * isDateString('sales'); // Returns: false
 * isDateString('iva 0'); // Returns: false
 * isDateString('123'); // Returns: false
 */
export const isDateString = (value: string): boolean => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const trimmedValue = value.trim();

  // Regex patterns for common date formats
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss (ISO format)
    /^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD: 2026/01/08
    /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY or MM/DD/YYYY: 08/01/2026 or 01/08/2026
    /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY or MM-DD-YYYY: 08-01-2026 or 01-08-2026
    /^\d{1,2}\/\d{1,2}\/\d{4}$/, // D/M/YYYY or M/D/YYYY (with single digits): 8/1/2026
    /^\d{1,2}-\d{1,2}-\d{4}$/, // D-M-YYYY or M-D-YYYY (with single digits): 8-1-2026
  ];

  const hasDateStructure = datePatterns.some((pattern) => pattern.test(trimmedValue));

  if (!hasDateStructure) {
    return false;
  }

  const parsedDate = dayjs(trimmedValue);

  return parsedDate.isValid();
};

/**
 * Formats a date string to DD/MM/YYYY format using dayjs
 *
 * If the value is not a valid date, returns the original string unchanged.
 *
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date string in DD/MM/YYYY format, or original string if invalid
 *
 * @example
 * formatDateValue('2025-01-15'); // Returns: '15/01/2025'
 * formatDateValue('sales'); // Returns: 'sales'
 */
export const formatDateValue = (dateString: string): string => {
  if (!isDateString(dateString)) {
    return dateString;
  }

  const formattedDate = dayjs(dateString).format('DD/MM/YYYY');

  return formattedDate;
};
